import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../../../chat/api";
import { Header } from "../../../../../components/Header/Header";
import AssignMembersModal from "./AssignMembersModal";
import LeftSection from "./LeftSection";
import RightSection from "./RightSection";

const ProposalSectionView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const orgId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const orgIdIndex = pathParts.indexOf("org") + 1;
    return pathParts[orgIdIndex] || null;
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [proposalData, setProposalData] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [folderId, setFolderId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [userPermission, setUserPermission] = useState(null);

  // Right Section State
  const [rightViewTab, setRightViewTab] = useState("comments");
  const [filterType, setFilterType] = useState("All");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Right Panel Interaction
  const [activeRightSection, setActiveRightSection] = useState(null);
  const [expandedMemberIds, setExpandedMemberIds] = useState(new Set());

  // Modal State
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [activeAssignSection, setActiveAssignSection] = useState({
    idx: null,
    title: "",
    sectionId: null,
  });

  const [analysisData, setAnalysisData] = useState({
    summary: "",
    strengths: [],
    deviations: [],
    recommendations: [],
  });
  const [complianceScore, setComplianceScore] = useState(0);

  const fetchSectionView = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await api.get(
        `/api/v1/rfp/folders/${projectId}/section-view/`
      );

      if (response.data) {
        const apiData = response.data;

        setFolderId(apiData.folder_id);
        setProjectName(apiData.folder_name || "Untitled Project");
        setUserPermission(apiData.user_permission || null);

        const formattedSections = (apiData.sections || []).map((section) => ({
          ...section,
          section_id: section.section_id || section.id,
          children: (section.subsections || []).map((sub) => ({
            ...sub,
            response: sub.response || "",
          })),
        }));

        const newProposalData = {
          proposal_content: formattedSections,
          proposal_analysis:
            location.state?.proposalData?.proposal_analysis || {},
        };

        setProposalData(newProposalData);

        if (location.state?.proposalData?.proposal_analysis) {
          const analysis = location.state.proposalData.proposal_analysis;
          setComplianceScore(analysis.compliance_score || 0);

          const parseListString = (str) => {
            if (!str) return [];
            return str
              .split(/\.\s+/)
              .filter((item) => item.trim().length > 0)
              .map((item) => item.trim() + (item.endsWith(".") ? "" : "."));
          };

          setAnalysisData({
            summary: analysis.summary || "",
            strengths: parseListString(analysis.strengths),
            deviations: parseListString(analysis.deviations),
            recommendations: parseListString(analysis.recommendations),
          });
        }
      }
    } catch (error) {
      console.error("Error fetching section view:", error);
      toast.error("Failed to load proposal sections");
    } finally {
      setLoading(false);
    }
  }, [projectId, location.state]);

  useEffect(() => {
    fetchSectionView();
  }, [fetchSectionView]);

  useEffect(() => {
    if (!proposalData || !proposalData.proposal_content) return;

    setExpandedSections((prev) => {
      if (
        Object.keys(prev).length === 0 &&
        proposalData.proposal_content.length > 0
      ) {
        const initialExpanded = {};
        proposalData.proposal_content.forEach((section, idx) => {
          initialExpanded[`section-${idx}`] = false;
        });
        return initialExpanded;
      }
      return prev;
    });

    setActiveRightSection((prev) => {
      if (!prev) return proposalData.proposal_content[0] || null;
      const updatedSection = proposalData.proposal_content.find(
        (s) => s.section_id === prev.section_id || s.id === prev.id
      );
      return updatedSection || prev;
    });
  }, [proposalData]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleSectionSelect = (section) => {
    setActiveRightSection(section);
  };

  const handleOpenAssignModal = (sectionIdx, sectionTitle, sectionId) => {
    setActiveAssignSection({
      idx: sectionIdx,
      title: sectionTitle,
      sectionId: sectionId,
    });
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setActiveAssignSection({ idx: null, title: "", sectionId: null });
  };

  const handleAssignSuccess = () => {
    fetchSectionView();
  };

  const handleSwitchToEditorView = () => {
    if (projectId && orgId) {
      navigate(
        `/rfp-projects/project/${projectId}/org/${orgId}/proposal/analysis`,
        {
          state: {
            proposalData,
            projectName,
            folderId,
            orgId,
          },
        }
      );
    } else {
      navigate(`/rfp-projects/project/${projectId}/proposal/analysis`, {
        state: {
          proposalData,
          projectName,
          folderId,
        },
      });
    }
  };

  const toggleMemberCard = (memberId) => {
    setExpandedMemberIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const getFilteredMembers = () => {
    if (!activeRightSection?.assigned_members) return [];
    if (filterType === "All") return activeRightSection.assigned_members;

    return activeRightSection.assigned_members.filter((member) => {
      const role = member.role || "";
      return role.toLowerCase() === filterType.toLowerCase();
    });
  };

  const displayedMembers = getFilteredMembers();

  const getInitials = (fullName) => {
    if (!fullName) return "U";
    const parts = fullName.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  if (loading) {
    return (
      <div className="h-screen">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C95EBE] mx-auto mb-4"></div>
            <p className="text-gray-600 font-outfit text-lg">
              Loading proposal sections...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#FEF9F7] flex flex-col relative">
      <AssignMembersModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        sectionTitle={activeAssignSection.title}
        sectionId={activeAssignSection.sectionId}
        folderId={projectId}
        onAssignSuccess={handleAssignSuccess}
      />

      <Header
        icon={"/arrow-back.svg"}
        name="Go to My Projects"
        onSwitchView={handleSwitchToEditorView}
        isProposalSectionView={true}
        projectId={projectId}
        orgId={orgId}
      />

      <div className="ml-[213px] mr-4 mt-4 mb-8 flex-1 flex gap-1 overflow-hidden">
        <LeftSection
          proposalData={proposalData}
          expandedSections={expandedSections}
          activeRightSection={activeRightSection}
          toggleSection={toggleSection}
          handleSectionSelect={handleSectionSelect}
          handleOpenAssignModal={handleOpenAssignModal}
          getInitials={getInitials}
          userPermission={userPermission}
        />

        <RightSection
          activeRightSection={activeRightSection}
          rightViewTab={rightViewTab}
          setRightViewTab={setRightViewTab}
          filterType={filterType}
          setFilterType={setFilterType}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          displayedMembers={displayedMembers}
          expandedMemberIds={expandedMemberIds}
          toggleMemberCard={toggleMemberCard}
          getInitials={getInitials}
          orgId={orgId}
          folderId={folderId}
          onRefresh={fetchSectionView}
          userPermission={userPermission}
        />
      </div>
    </div>
  );
};

export default ProposalSectionView;
